/* ──────────────────────────────────────────────────────────────
   Setup/Types/index.tsx · Item types & field builder
   React Native + TypeScript — mirrors view_setup.jsx TypesView
   ────────────────────────────────────────────────────────────── */

import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  Image,
  ScrollView,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  ViewStyle,
} from 'react-native';

import { useTheme } from '../../context/themeContext';
import { useResponsive } from '../../lib/responsive';
import { useNavigation } from '@react-navigation/native';
import { useReloadOnFocus } from '../../navigation/useReloadOnFocus';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { AppStackParamList } from '../../navigation/types';
import { FieldDef } from '../../data/mockData';
import { useFieldTypes } from '../../hooks/useFieldTypes';
import { getItemTypes, getItemType, updateItemType, uploadTypeImage, getItemTypeFields, addItemTypeField, updateItemTypeField, deleteItemTypeField, deleteItemType } from '../../services/catalog/itemType';
import { handleApiError } from '../../utils/errorHandler';
import type { ItemType as ApiItemType } from '../../schemas/catalog/itemType';
import NewTypeWizard from '../../components/newTypeWizard';
import {
  Icon,
  Button,
  Chip,
  Input,
  Switch,
  Modal,
  Empty,
  Popover,
  MenuItem,
  MenuSeparator,
  ConfirmDialog,
  SuccessDialog,
  useToast,
} from '../../components/ui';


// ── AddFieldModal ─────────────────────────────────────────────

interface AddFieldModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (f: FieldDef) => void;
}

function AddFieldModal({ open, onClose, onAdd }: AddFieldModalProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;
  const fieldTypes = useFieldTypes();
  const [label, setLabel] = useState('');
  const [fieldType, setFieldType] = useState('TEXT');

  const key =
    label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/, '') || 'new_field';

  const footer = (
    <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
      <Button onPress={onClose}>Cancel</Button>
      <Button
        kind="accent"
        icon="plus"
        disabled={!label}
        onPress={() => onAdd({ key, type: fieldType, label })}
      >
        Add field
      </Button>
    </View>
  );

  return (
    <Modal open={open} onClose={onClose} size="sm" title="Add a field" footer={footer}>
      <View style={{ gap: 12 }}>
        <Input
          label="Field label"
          placeholder="e.g. Allergen info"
          value={label}
          onChangeText={setLabel}
          autoFocus
        />
        {!!label && (
          <Text
            style={{
              fontFamily: theme.font.mono,
              fontSize: fontSize.xs,
              color: colors.ink3,
            }}
          >
            key: {key}
          </Text>
        )}
        {/* type-grid: 3 cols */}
        <View>
          <Text
            style={{
              fontSize: fontSize.sm,
              fontWeight: fontWeight.medium,
              color: colors.ink2,
              fontFamily: theme.font.sans,
              marginBottom: 8,
            }}
          >
            Field type
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            {fieldTypes.picker.map(ft => {
              const isActive = fieldType === ft.type;
              return (
                <TouchableOpacity
                  key={ft.type}
                  onPress={() => setFieldType(ft.type)}
                  style={{
                    borderWidth: 1,
                    borderColor: isActive ? colors.accent : colors.line,
                    borderRadius: radii.r2,
                    padding: 10,
                    width: '30%',
                    alignItems: 'center',
                    gap: 6,
                    backgroundColor: isActive ? colors.accentSoft : colors.card,
                    ...Platform.select({ web: { cursor: 'pointer' } as ViewStyle, default: {} }),
                  }}
                >
                  <Icon name={ft.icon} size={16} color={isActive ? colors.accentInk : colors.ink2} />
                  <Text
                    style={{
                      fontSize: fontSize.sm,
                      color: isActive ? colors.accentInk : colors.ink2,
                      fontFamily: theme.font.sans,
                      textAlign: 'center',
                    }}
                  >
                    {ft.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      </View>
    </Modal>
  );
}

// ── FieldBuilder ──────────────────────────────────────────────

interface FieldBuilderProps {
  typeId: string;
  typeName: string;
  onBack: () => void;
}

// Map a backend FieldDefinition → the UI's FieldDef shape.
const mapField = (fd: any): FieldDef => ({
  key: fd.fieldKey,
  type: fd.fieldType,
  label: fd.fieldKey,
  required: !!fd.isRequired,
  location_scoped: !!fd.isLocationScoped,
  localized: !!fd.isLocalized,
  options: fd.config?.options ?? [],
  unit: fd.config?.unit,
} as FieldDef);

function FieldBuilder({ typeId, typeName, onBack }: FieldBuilderProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;
  const fieldTypes = useFieldTypes();
  const toast = useToast();
  const { isMobile } = useResponsive();

  // Real fields from the catalog API (GET /item-types/:id/fields).
  const [fields, setFields] = useState<FieldDef[]>([]);
  const [selKey, setSelKey] = useState<string | undefined>(undefined);
  const [loadingFields, setLoadingFields] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [delField, setDelField] = useState<any | null>(null);
  const [deletingField, setDeletingField] = useState(false);

  // Editable type header (name + image), loaded from GET /item-types/:id.
  const [name, setName] = useState(typeName);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imgBusy, setImgBusy] = useState(false);
  // "Saved" success dialog shown when the user clicks Save.
  const [savedOpen, setSavedOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    getItemType(typeId)
      .then(t => { if (alive) { setName(t.name); setImageUrl((t as any).imageUrl ?? null); } })
      .catch(() => {});
    return () => { alive = false; };
  }, [typeId]);

  // Persist a renamed type on blur (skip empty / unchanged).
  const saveName = async () => {
    const trimmed = name.trim();
    if (!trimmed) { setName(typeName); return; }
    try {
      const updated = await updateItemType(typeId, { name: trimmed });
      setName(updated.name);
      toast.success('Name updated');
    } catch (err) {
      handleApiError(err, 'Could not rename type');
    }
  };

  // Web file picker → presigned upload → PATCH imageKey → show fresh url.
  const pickTypeImage = () => {
    if (Platform.OS !== 'web') return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setImgBusy(true);
      try {
        const key = await uploadTypeImage(file);
        const updated = await updateItemType(typeId, { imageKey: key });
        setImageUrl((updated as any).imageUrl ?? null);
        toast.success('Image updated');
      } catch (err) {
        handleApiError(err, 'Could not update image');
      } finally {
        setImgBusy(false);
      }
    };
    input.click();
  };

  const loadFields = useCallback(async () => {
    setLoadingFields(true);
    try {
      const res = await getItemTypeFields(typeId);
      const mapped = ((res as any).items ?? []).map(mapField);
      setFields(mapped);
      setSelKey(prev => prev ?? mapped[0]?.key);
    } catch (err) {
      handleApiError(err, 'Could not load fields');
    } finally {
      setLoadingFields(false);
    }
  }, [typeId]);

  useEffect(() => { loadFields(); }, [loadFields]);

  // Explicit "Save" for the selected field — persists its full current settings.
  const [savingField, setSavingField] = useState(false);
  const saveField = async (field?: FieldDef) => {
    if (!field) return;
    setSavingField(true);
    try {
      const cleanOptions = (field.options ?? []).map(o => o.trim()).filter(Boolean);
      const cleanUnit = (field.unit ?? '').trim();
      await updateItemTypeField(typeId, field.key, {
        fieldType: field.type,
        isRequired: !!field.required,
        isLocationScoped: !!field.location_scoped,
        isLocalized: !!field.localized,
        config: {
          ...(cleanOptions.length ? { options: cleanOptions } : {}),
          ...(cleanUnit ? { unit: cleanUnit } : {}),
        },
      });
      toast.success('Field saved');
    } catch (err) {
      handleApiError(err, 'Could not save field');
    } finally {
      setSavingField(false);
    }
  };

  // Persist a field-property change (type/required/location_scoped/localized).
  // Optimistically updates the local list, then PATCHes the backend.
  const patchField = async (
    fieldKey: string,
    uiPatch: Partial<{ type: string; required: boolean; location_scoped: boolean; localized: boolean; options: string[]; unit: string }>,
  ) => {
    // Apply the change locally, then derive the config (options/unit) for this
    // field from the just-updated list so a type+options change persist together.
    let nextField: FieldDef | undefined;
    setFields(prev => {
      const next = prev.map(f => (f.key === fieldKey ? { ...f, ...uiPatch } : f));
      nextField = next.find(f => f.key === fieldKey);
      return next;
    });
    const touchesConfig = uiPatch.options !== undefined || uiPatch.unit !== undefined;
    // Drop blank option rows (kept locally for editing) — the API requires
    // each option to be non-empty.
    const cleanOptions = (nextField?.options ?? []).map(o => o.trim()).filter(Boolean);
    const cleanUnit = (nextField?.unit ?? '').trim();
    try {
      await updateItemTypeField(typeId, fieldKey, {
        ...(uiPatch.type !== undefined ? { fieldType: uiPatch.type } : {}),
        ...(uiPatch.required !== undefined ? { isRequired: uiPatch.required } : {}),
        ...(uiPatch.location_scoped !== undefined ? { isLocationScoped: uiPatch.location_scoped } : {}),
        ...(uiPatch.localized !== undefined ? { isLocalized: uiPatch.localized } : {}),
        ...(touchesConfig
          ? {
              config: {
                ...(cleanOptions.length ? { options: cleanOptions } : {}),
                ...(cleanUnit ? { unit: cleanUnit } : {}),
              },
            }
          : {}),
      });
    } catch (err) {
      handleApiError(err, 'Could not update field');
      loadFields(); // revert to server truth on failure
    }
  };

  const confirmDeleteField = async () => {
    if (!delField) return;
    setDeletingField(true);
    try {
      await deleteItemTypeField(typeId, delField.key);
      toast.success('Field deleted');
      setSelKey(prev => (prev === delField.key ? undefined : prev));
      setDelField(null);
      await loadFields(); // reflect the real DB state
    } catch (err) {
      handleApiError(err, 'Could not delete field');
    } finally {
      setDeletingField(false);
    }
  };

  const sel = fields.find(f => f.key === selKey);
  // Resolve label/icon for any field's type — including hidden types (RELATION,
  // JSON) so pre-existing fields still display correctly.
  const ftMeta = (t: string) => fieldTypes.meta(t);

  // Premium header buttons (your Button component + a hover lift).
  const blackBtnStyle: ViewStyle = {
    ...Platform.select({
      web: { boxShadow: '0 1px 2px rgba(17,24,39,0.18)', transition: 'all 160ms ease' } as any,
      default: {},
    }),
  };

  const move = (i: number, dir: number) => {
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    const n = [...fields];
    [n[i], n[j]] = [n[j], n[i]];
    setFields(n);
  };

  const sectionCardStyle: ViewStyle = {
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.r3,
    ...(shadows.sm as any),
    marginBottom: 0,
  };

  // On web use position sticky for inspector
  const inspectorStyle: ViewStyle = {
    ...sectionCardStyle,
    ...Platform.select({
      web: { position: 'sticky', top: 0 } as any,
      default: {},
    }),
  };

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: colors.paper }}
      contentContainerStyle={{ padding: 24, paddingBottom: 64, maxWidth: 1320 }}
    >
      {/* Breadcrumb */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <TouchableOpacity onPress={onBack}>
          <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans }}>
            Item types
          </Text>
        </TouchableOpacity>
        <Icon name="chev-r" size={11} color={colors.ink3} />
        <Text style={{ fontSize: fontSize.sm, color: colors.ink, fontFamily: theme.font.sans }}>
          {name}
        </Text>
      </View>

      {/* page-title-row */}
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
        <View style={{ flex: 1, minWidth: 220, flexDirection: 'row', alignItems: 'center', gap: 24 }}>
          {/* Editable type image — click to change */}
          <TouchableOpacity
            onPress={pickTypeImage}
            disabled={imgBusy}
            activeOpacity={0.8}
            style={{
              width: 104,
              height: 104,
              borderRadius: radii.r3,
              borderWidth: 2,
              borderColor: colors.ink,
              backgroundColor: colors.paper2,
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              ...Platform.select({ web: { cursor: 'pointer' } as ViewStyle, default: {} }),
            }}
          >
            {imgBusy ? (
              <ActivityIndicator size="small" color={colors.accent} />
            ) : imageUrl ? (
              <Image source={{ uri: imageUrl }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
            ) : (
              <Text style={{ fontSize: 46 }}>📦</Text>
            )}
            {/* upload badge */}
            <View style={{ position: 'absolute', right: 5, bottom: 5, width: 26, height: 26, borderRadius: 13, backgroundColor: colors.accent, borderWidth: 2, borderColor: colors.card, alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="upload" size={12} color={colors.white} />
            </View>
          </TouchableOpacity>
          <View style={{ minWidth: 0 }}>
            <View style={{ width: 360, maxWidth: '100%' }}>
              <Input
                value={name}
                onChangeText={setName}
                onBlur={saveName}
                placeholder="Type name"
                inputStyle={{ fontSize: fontSize.xl, fontWeight: fontWeight.semibold, fontFamily: theme.font.sans } as any}
              />
            </View>
            <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans, marginTop: 4, maxWidth: 420 }}>
              Click the image to change it. Edit the name above. Fields below render in the item editor in this order.
            </Text>
          </View>
        </View>
        <View style={{ flexDirection: 'row', gap: 14 }}>
          <Button kind="primary" icon="check" hoverLift onPress={() => setSavedOpen(true)} style={blackBtnStyle}>
            Save
          </Button>
          <Button kind="primary" icon="plus" hoverLift onPress={() => setAddOpen(true)} style={blackBtnStyle}>
            Add field
          </Button>
        </View>
      </View>

      {/* fb-grid: 1fr 360px — on narrow screens stack vertically */}
      <View
        style={Platform.OS === 'web' && !isMobile
          ? ({ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 20, alignItems: 'start' } as any)
          : { gap: 20 }}
      >
        {/* Left: field list */}
        <View>
          {fields.map((f, i) => {
            const isSelected = selKey === f.key;
            const meta = ftMeta(f.type);
            return (
              <TouchableOpacity
                key={f.key}
                activeOpacity={0.85}
                onPress={() => setSelKey(f.key)}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 12,
                  paddingVertical: 11,
                  paddingHorizontal: 14,
                  borderWidth: 1,
                  borderColor: isSelected ? colors.accent : colors.line,
                  borderRadius: radii.r2,
                  backgroundColor: colors.card,
                  marginBottom: 8,
                  ...(isSelected ? (shadows.base as any) : (shadows.sm as any)),
                  ...Platform.select({ web: { cursor: 'grab' } as any, default: {} }),
                }}
              >
                {/* field-grip */}
                <View style={{ gap: 0 }}>
                  <TouchableOpacity onPress={() => move(i, -1)}>
                    <Icon name="chev-u" size={13} color={i === 0 ? colors.ink4 : colors.ink3} />
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => move(i, 1)}>
                    <Icon name="chev-d" size={13} color={i === fields.length - 1 ? colors.ink4 : colors.ink3} />
                  </TouchableOpacity>
                </View>

                {/* field type icon */}
                <Icon name={meta.icon} size={16} color={colors.ink3} />

                {/* field-info */}
                <View style={{ flex: 1, minWidth: 0 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <Text style={{ fontWeight: fontWeight.medium, color: colors.ink, fontFamily: theme.font.sans, fontSize: fontSize.md }}>
                      {f.label}
                    </Text>
                    {f.required && (
                      <Text style={{ color: colors.danger, fontSize: fontSize.md }}>*</Text>
                    )}
                  </View>
                  <Text style={{ fontFamily: theme.font.mono, fontSize: fontSize.xs, color: colors.ink3 }}>
                    {f.key}
                  </Text>
                </View>

                {/* chips + type pill */}
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                  {f.localized && <Chip tone="info" icon={<Icon name="translate" size={11} color={colors.info} />}>i18n</Chip>}
                  {f.location_scoped && <Chip tone="neutral" icon={<Icon name="map-pin" size={11} color={colors.ink2} />}>loc</Chip>}
                  {/* field-type-pill */}
                  <View
                    style={{
                      backgroundColor: colors.paper2,
                      borderWidth: 1,
                      borderColor: colors.line,
                      borderRadius: radii.r1,
                      paddingHorizontal: 6,
                      paddingVertical: 1,
                    }}
                  >
                    <Text style={{ fontFamily: theme.font.mono, fontSize: fontSize.xs, color: colors.ink2 }}>
                      {meta.label}
                    </Text>
                  </View>

                  {/* delete field — red bin */}
                  <Button
                    kind="danger"
                    size="xs"
                    iconOnly
                    icon="trash"
                    aria-label="Delete field"
                    onPress={() => setDelField(f)}
                  />
                </View>
              </TouchableOpacity>
            );
          })}

          {/* dropzone add button */}
          <TouchableOpacity
            onPress={() => setAddOpen(true)}
            style={{
              borderWidth: 1,
              borderStyle: 'dashed',
              borderColor: colors.lineStrong,
              borderRadius: radii.r3,
              padding: 16,
              marginTop: 4,
              alignItems: 'center',
              flexDirection: 'row',
              justifyContent: 'center',
              gap: 8,
              backgroundColor: colors.paper2,
              ...Platform.select({ web: { cursor: 'pointer' } as ViewStyle, default: {} }),
            }}
          >
            <Icon name="plus" size={16} color={colors.ink3} />
            <Text style={{ fontSize: fontSize.md, color: colors.ink3, fontFamily: theme.font.sans }}>
              Add another field
            </Text>
          </TouchableOpacity>
        </View>

        {/* Right: field inspector */}
        <View style={inspectorStyle}>
          {/* section-head */}
          <View style={{ padding: 13, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: colors.line, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
              <Icon name="settings" size={15} color={colors.ink2} />
              <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: theme.font.sans }}>
                Field settings
              </Text>
            </View>
            {sel && (
              <Button
                kind="primary"
                size="sm"
                icon="check"
                disabled={savingField}
                onPress={() => saveField(sel)}
              >
                {savingField ? 'Saving…' : 'Save'}
              </Button>
            )}
          </View>

          {/* section-body */}
          <View style={{ padding: 16 }}>
            {sel ? (
              <View style={{ gap: 12 }}>
                {/* Field key — read-only identity. It's the key every saved
                    item value is stored under, so it can't be renamed here. */}
                <View>
                  <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: theme.font.sans, marginBottom: 6 }}>
                    Field key
                  </Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 9, paddingHorizontal: 12, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, backgroundColor: colors.paper2 }}>
                    <Icon name="lock" size={13} color={colors.ink3} />
                    <Text style={{ flex: 1, fontFamily: theme.font.mono, fontSize: fontSize.sm, color: colors.ink2 }}>
                      {sel.key}
                    </Text>
                  </View>
                  <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.sans, marginTop: 4 }}>
                    Used by the API, imports & webhooks. Fixed once created.
                  </Text>
                </View>

                {/* Field type grid */}
                <View>
                  <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: theme.font.sans, marginBottom: 8 }}>
                    Field type
                  </Text>
                  {/* type-grid: 3 cols — creatable field types (DB-backed) */}
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                    {fieldTypes.picker.map(ft => {
                      const isActive = sel.type === ft.type;
                      return (
                        <TouchableOpacity
                          key={ft.type}
                          onPress={() => patchField(sel.key, { type: ft.type })}
                          style={{
                            borderWidth: 1,
                            borderColor: isActive ? colors.accent : colors.line,
                            borderRadius: radii.r2,
                            padding: 10,
                            width: '30%',
                            alignItems: 'center',
                            gap: 6,
                            backgroundColor: isActive ? colors.accentSoft : colors.card,
                            ...Platform.select({ web: { cursor: 'pointer' } as ViewStyle, default: {} }),
                          }}
                        >
                          <Icon name={ft.icon} size={16} color={isActive ? colors.accentInk : colors.ink2} />
                          <Text style={{ fontSize: fontSize.sm, color: isActive ? colors.accentInk : colors.ink2, fontFamily: theme.font.sans, textAlign: 'center' }}>
                            {ft.label}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>

                {(sel.type === 'ENUM' || sel.type === 'MULTI_SELECT') && (
                  <View>
                    <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: theme.font.sans, marginBottom: 8 }}>
                      Dropdown options
                    </Text>
                    <View style={{ gap: 6 }}>
                      {(sel.options || []).map((opt, i) => (
                        <View key={`${sel.key}-opt-${i}`} style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                          <View style={{ flex: 1 }}>
                            <Input
                              value={opt}
                              placeholder={`Option ${i + 1}`}
                              onChangeText={(v: string) => {
                                const next = [...(sel.options || [])];
                                next[i] = v;
                                // Update locally while typing; persist on blur.
                                setFields(prev => prev.map(f => (f.key === sel.key ? { ...f, options: next } : f)));
                              }}
                              onBlur={() =>
                                patchField(sel.key, { options: (sel.options || []).map(o => o.trim()).filter(Boolean) })
                              }
                            />
                          </View>
                          <Button
                            kind="danger"
                            size="xs"
                            iconOnly
                            icon="trash"
                            aria-label="Remove option"
                            onPress={() => patchField(sel.key, { options: (sel.options || []).filter((_, j) => j !== i) })}
                          />
                        </View>
                      ))}
                    </View>
                    <TouchableOpacity
                      onPress={() => patchField(sel.key, { options: [...(sel.options || []), ''] })}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 6,
                        marginTop: 8,
                        paddingVertical: 8,
                        paddingHorizontal: 10,
                        borderWidth: 1,
                        borderStyle: 'dashed',
                        borderColor: colors.lineStrong,
                        borderRadius: radii.r2,
                        alignSelf: 'flex-start',
                        ...Platform.select({ web: { cursor: 'pointer' } as ViewStyle, default: {} }),
                      }}
                    >
                      <Icon name="plus" size={14} color={colors.ink3} />
                      <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans }}>Add option</Text>
                    </TouchableOpacity>
                    <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.sans, marginTop: 6 }}>
                      These appear in the dropdown when editing an item.
                    </Text>
                  </View>
                )}
                {sel.type === 'NUMBER' && (
                  <Input
                    label="Unit"
                    value={sel.unit || ''}
                    placeholder="e.g. kcal, g, units"
                    onChangeText={(v: string) =>
                      setFields(prev => prev.map(f => (f.key === sel.key ? { ...f, unit: v } : f)))
                    }
                    onBlur={() => patchField(sel.key, { unit: (sel.unit || '').trim() })}
                  />
                )}

                {/* Divider */}
                <View style={{ height: 1, backgroundColor: colors.line, marginVertical: 4 }} />

                <Switch
                  label="Required"
                  checked={!!sel.required}
                  onChange={() => patchField(sel.key, { required: !sel.required })}
                />

                <View>
                  <Switch
                    label="Varies by location"
                    checked={!!sel.location_scoped}
                    onChange={() => patchField(sel.key, { location_scoped: !sel.location_scoped })}
                  />
                  <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.sans, marginLeft: 40, marginTop: 2 }}>
                    Editors can set a different value per branch.
                  </Text>
                </View>

                {(sel.type === 'TEXT' || sel.type === 'RICH_TEXT') && (
                  <View>
                    <Switch
                      label="Translatable"
                      checked={!!sel.localized}
                      onChange={() => patchField(sel.key, { localized: !sel.localized })}
                    />
                    <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.sans, marginLeft: 40, marginTop: 2 }}>
                      Adds a value per language; missing ones fall back to the default.
                    </Text>
                  </View>
                )}

                {/* Divider */}
                <View style={{ height: 1, backgroundColor: colors.line, marginVertical: 4 }} />

                <Button
                  kind="danger"
                  size="sm"
                  icon="trash"
                  onPress={() => setDelField(sel)}
                  style={{ alignSelf: 'flex-start' }}
                >
                  Remove field
                </Button>
              </View>
            ) : (
              <Empty title="Select a field">
                Click a field on the left to edit it.
              </Empty>
            )}
          </View>
        </View>
      </View>

      <AddFieldModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={async (f) => {
          setAddOpen(false);
          try {
            await addItemTypeField(typeId, { fieldKey: f.key, fieldType: f.type as any });
            toast.success('Field added');
            await loadFields(); // persist + reflect the real DB state
            setSelKey(f.key);
          } catch (err) {
            handleApiError(err, 'Could not add field');
          }
        }}
      />

      <ConfirmDialog
        visible={!!delField}
        title="Delete field?"
        message={delField ? `Field "${delField.key}" and all its stored values will be permanently deleted from this type.` : ''}
        confirmText={deletingField ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        onConfirm={confirmDeleteField}
        onCancel={() => setDelField(null)}
        onClose={() => setDelField(null)}
      />

      {/* Save confirmation — every edit already persisted live; this confirms
          to the user and returns to the list. */}
      <SuccessDialog
        visible={savedOpen}
        title="Changes saved"
        message={`"${name}" is up to date.`}
        summary={[`${fields.length} field${fields.length === 1 ? '' : 's'}`, ...(imageUrl ? ['Image set'] : [])]}
        primaryAction={{ label: 'Done', onPress: () => { setSavedOpen(false); onBack(); } }}
        secondaryAction={{ label: 'Keep editing', onPress: () => setSavedOpen(false) }}
        onClose={() => setSavedOpen(false)}
      />
    </ScrollView>
  );
}

// ── TypesView (main export) ───────────────────────────────────

export default function TypesView() {
  const { theme } = useTheme();
  const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;
  const toast = useToast();

  const [editing, setEditing] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  // Live item types from sk-catalog-api (GET /api/v1/item-types).
  const [types, setTypes] = useState<ApiItemType[]>([]);
  const [loading, setLoading] = useState(true);

  // Delete-a-type confirmation.
  const [delType, setDelType] = useState<ApiItemType | null>(null);
  const [deletingType, setDeletingType] = useState(false);

  const loadTypes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getItemTypes();
      setTypes(res.items ?? []);
    } catch (err) {
      handleApiError(err, 'Could not load item types');
    } finally {
      setLoading(false);
    }
  }, []);

  // Refetch on focus so a type created/edited elsewhere shows up on return.
  useReloadOnFocus(loadTypes);

  // After the wizard creates a type: refresh the list, and jump straight into
  // the field builder if the user chose "Manage fields".
  const handleCreated = useCallback(
    (created: ApiItemType, opts?: { manage?: boolean }) => {
      loadTypes();
      if (opts?.manage) setEditing(created.id);
    },
    [loadTypes],
  );

  // Delete a whole item type after confirmation. The API refuses (409) if
  // items still use it — surface that as a clear message, don't crash.
  const confirmDeleteType = useCallback(async () => {
    if (!delType) return;
    setDeletingType(true);
    try {
      await deleteItemType(delType.id);
      toast.success('Item type deleted');
      setDelType(null);
      await loadTypes();
    } catch (err) {
      handleApiError(err, 'Could not delete item type');
    } finally {
      setDeletingType(false);
    }
  }, [delType, loadTypes, toast]);

  if (editing) {
    return (
      <FieldBuilder
        typeId={editing}
        typeName={types.find(t => t.id === editing)?.name ?? 'Type'}
        onBack={() => { setEditing(null); loadTypes(); }}
      />
    );
  }

  // The "Bundle" item type is an internal implementation detail of the Bundles
  // feature (auto-created so named bundles can be catalog items). It must NOT
  // surface here as a user-facing type — bundles are managed on the Bundles
  // screen, not defined as an item type. Hide it, mirroring the Items screen.
  const visibleTypes = types.filter(t => (t.name || '').toLowerCase() !== 'bundle');

  const sectionCardStyle: ViewStyle = {
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.r3,
    ...(shadows.sm as any),
    marginBottom: 0,
  };

  return (
    <ScrollView style={{ flex: 1, backgroundColor: colors.paper }} contentContainerStyle={{ padding: 24, paddingBottom: 64, maxWidth: 1320 }}>
      {/* page-title-row */}
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
        <View style={{ flex: 1, minWidth: 240 }}>
          <Text style={{ fontSize: fontSize['3xl'], fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: theme.font.sans, letterSpacing: -0.02 * fontSize['3xl'] }}>
            Item types &amp; fields
          </Text>
          <Text style={{ fontSize: fontSize.md, color: colors.ink3, fontFamily: theme.font.sans, marginTop: 4 }}>
            Define what an item looks like for your business. Each type is a set of fields — no code, no database terms.
          </Text>
        </View>
        <Button
          kind="primary"
          size="sm"
          icon="plus"
          hoverLift
          onPress={() => setWizardOpen(true)}
          style={{
            ...Platform.select({
              web: { boxShadow: '0 1px 2px rgba(17,24,39,0.18)', transition: 'all 160ms ease' } as any,
              default: {},
            }),
          }}
        >
          Add New Type
        </Button>
      </View>

      {/* t-stack gap 12 */}
      {loading ? (
        <View style={{ paddingVertical: 48, alignItems: 'center' }}>
          <ActivityIndicator color={colors.accent} />
        </View>
      ) : visibleTypes.length === 0 ? (
        <Empty title="No item types yet">
          Create your first type from a template to start building your catalog.
        </Empty>
      ) : (
        <View style={{ gap: 12, maxWidth: 760, width: '100%' }}>
          {visibleTypes.map(t => (
            <View key={t.id} style={sectionCardStyle}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16, padding: 18 }}>
                {/* Clicking the card body opens Manage fields. The ⋯ menu is a
                    sibling so its actions don't trigger this. */}
                <TouchableOpacity
                  activeOpacity={0.7}
                  onPress={() => setEditing(t.id)}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 16,
                    ...Platform.select({ web: { cursor: 'pointer' } as ViewStyle, default: {} }),
                  }}
                >
                  {t.imageUrl ? (
                    <Image
                      source={{ uri: t.imageUrl }}
                      style={{ width: 92, height: 92, borderRadius: radii.r3, backgroundColor: colors.paper2 }}
                      resizeMode="cover"
                    />
                  ) : (
                    <View style={{ width: 92, height: 92, borderRadius: radii.r3, backgroundColor: colors.paper2, alignItems: 'center', justifyContent: 'center' }}>
                      <Text style={{ fontSize: 46 }}>📦</Text>
                    </View>
                  )}
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <Text style={{ fontWeight: fontWeight.semibold, fontSize: fontSize.lg, color: colors.ink, fontFamily: theme.font.sans }}>
                        {t.name}
                      </Text>
                      <Chip tone="info" icon={<Icon name="sparkles" size={11} color={colors.info} />}>Live</Chip>
                    </View>
                    <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans, marginTop: 2 }}>
                      {t.description || 'No description'}
                    </Text>
                    {/* Field chips — first 4 keys + overflow count */}
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 10, alignItems: 'center' }}>
                      {Array.isArray(t.fields) && t.fields.length > 0 ? (
                        <>
                          {t.fields.slice(0, 4).map(f => (
                            <Chip key={f.fieldKey} tone="outline">{f.fieldKey}</Chip>
                          ))}
                          {t.fields.length > 4 && (
                            <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.sans }}>
                              +{t.fields.length - 4}
                            </Text>
                          )}
                        </>
                      ) : (
                        <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.sans }}>
                          No fields yet
                        </Text>
                      )}
                    </View>
                  </View>
                </TouchableOpacity>

                <Popover
                  align="right"
                  width={180}
                  trigger={<Button kind="ghost" iconOnly icon="more" aria-label="More" />}
                >
                  {(close: () => void) => (
                    <>
                      <MenuItem icon="sliders" onPress={() => { close(); setEditing(t.id); }}>
                        Manage fields
                      </MenuItem>
                      <MenuItem icon="box" onPress={() => { close(); navigation.navigate('items', { type: t.name }); }}>
                        View items
                      </MenuItem>
                      <MenuSeparator />
                      <MenuItem icon="trash" danger onPress={() => { close(); setDelType(t); }}>
                        Delete
                      </MenuItem>
                    </>
                  )}
                </Popover>
              </View>
            </View>
          ))}
        </View>
      )}

      <NewTypeWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={handleCreated}
      />

      <ConfirmDialog
        visible={!!delType}
        title="Delete item type?"
        message={delType ? `"${delType.name}" and all its fields will be permanently deleted. This can't be undone.` : ''}
        confirmText={deletingType ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        onConfirm={confirmDeleteType}
        onCancel={() => setDelType(null)}
        onClose={() => setDelType(null)}
      />
    </ScrollView>
  );
}
